import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Briefcase, TrendingUp, Calendar, CheckCircle2, UserCheck, Bell, BellDot, Check } from 'lucide-react';

import UnifiedCard from '../../../components/unified/UnifiedCard';
import UnifiedButton from '../../../components/unified/UnifiedButton';
import { typography, colors, icons } from '../../../styles/design-tokens';
import LoadingState from '../../../components/unified/LoadingState';
import EmptyState from '../../../components/unified/EmptyState';
import { fetchUserNotifications, markNotificationAsRead } from '../../../services/applicationsService';
import Button from '../../../components/ui/Button';

// Add this block - utility function for classnames
const cn = (...classes) => classes?.filter(Boolean)?.join(' ');

const DashboardOverview = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Add this block - mock data for companyData and userRole
  const companyData = { name: 'Your Company' };
  const userRole = 'admin';
  
  // Simulate data loading
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        setIsLoading(false);
      } catch (err) {
        setError(err?.message || 'Failed to load dashboard data');
        setIsLoading(false);
      }
    };

    loadDashboardData();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      const loadNotifications = async () => {
        try {
          const userId = 'current-user-id'; // Get from auth context
          const notifs = await fetchUserNotifications(userId, false);
          setNotifications(notifs);
          setUnreadCount(notifs?.filter(n => !n?.is_read)?.length || 0);
        } catch (error) {
          console.error('Error loading notifications:', error);
        }
      };
      loadNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => prev?.map(n => 
        n?.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.4
      }
    }
  };

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;

  // Mock stats data
  const statsData = [
    {
      icon: Briefcase,
      label: 'Active Jobs',
      value: '24',
      change: '+3 this week',
      color: 'bg-accent/10 text-accent',
      trending: 'up'
    },
    {
      icon: Users,
      label: 'Total Applicants',
      value: '387',
      change: '+52 this week',
      color: 'bg-green-500/10 text-green-600',
      trending: 'up'
    },
    {
      icon: CheckCircle2,
      label: 'Interviews Scheduled',
      value: '18',
      change: '6 pending',
      color: 'bg-blue-500/10 text-blue-600',
      trending: 'neutral'
    },
    {
      icon: TrendingUp,
      label: 'Conversion Rate',
      value: '12.4%',
      change: '+2.1% vs last month',
      color: 'bg-purple-500/10 text-purple-600',
      trending: 'up'
    }
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        {/* Welcome Section Skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
        </div>

        {/* Stats Cards Loading */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <LoadingState variant="stat-card" count={4} />
        </div>

        {/* Recent Activity Loading */}
        <div>
          <div className="h-6 bg-muted rounded w-1/4 mb-4 animate-pulse"></div>
          <LoadingState variant="list" count={3} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load dashboard"
        description={error}
        actionLabel="Try Again"
        onAction={() => window.location?.reload()}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with Notifications */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard Overview</h1>
          <p className="text-muted-foreground">Welcome back! Here's what's happening with your hiring.</p>
        </div>

        {/* Notifications Bell */}
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative"
          >
            {unreadCount > 0 ? (
              <>
                <BellDot className="w-5 h-5 text-primary" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              </>
            ) : (
              <Bell className="w-5 h-5" />
            )}
          </Button>

          {/* Notifications Dropdown */}
          <AnimatePresence>
            {showNotifications && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute right-0 mt-2 w-96 bg-background border border-border rounded-lg shadow-xl z-50"
              >
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {unreadCount} unread
                      </span>
                    )}
                  </div>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {notifications?.length > 0 ? (
                    notifications?.slice(0, 10)?.map(notification => (
                      <div
                        key={notification?.id}
                        className={cn(
                          'p-4 border-b border-border hover:bg-muted/30 transition-colors',
                          !notification?.is_read && 'bg-primary/5'
                        )}
                      >
                        <div className="flex items-start justify-between space-x-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground mb-1">
                              {notification?.message}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatNotificationTime(notification?.created_at)}
                            </p>
                          </div>
                          {!notification?.is_read && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleMarkAsRead(notification?.id)}
                              className="h-6 w-6 flex-shrink-0"
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-3 opacity-50" />
                      <p className="text-muted-foreground text-sm">No notifications yet</p>
                    </div>
                  )}
                </div>

                {notifications?.length > 10 && (
                  <div className="p-3 border-t border-border text-center">
                    <Button variant="ghost" size="sm" className="text-primary">
                      View All Notifications
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* Welcome Section */}
      <motion.div variants={prefersReducedMotion ? {} : itemVariants}>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <h1 className={`${typography?.h1} ${colors?.text?.primary}`}>
              {companyData?.name} Dashboard
            </h1>
            <p className={`${typography?.body?.default} ${colors?.text?.secondary}`}>
              {userRole === 'admin' ? 'Company Overview & Analytics' : 'Your Recruitment Activity'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <UnifiedButton variant="secondary" size="medium">
              <Calendar className="w-4 h-4 mr-2" />
              Last 30 Days
            </UnifiedButton>
            <UnifiedButton variant="primary" size="medium">
              Post New Job
            </UnifiedButton>
          </div>
        </div>
      </motion.div>
      {/* Stats Grid */}
      <motion.div 
        variants={prefersReducedMotion ? {} : itemVariants}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
      >
        <UnifiedCard variant="default">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-accent/10 rounded-lg">
              <Briefcase className={`${icons?.lg} text-accent`} />
            </div>
            <div>
              <p className={`${typography?.body?.small} ${colors?.text?.secondary}`}>Active Jobs</p>
              <p className={`${typography?.h1} ${colors?.text?.primary}`}>{statsData?.[0]?.value}</p>
            </div>
          </div>
        </UnifiedCard>

        <UnifiedCard variant="default">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Users className={`${icons?.lg} text-blue-600 dark:text-blue-400`} />
            </div>
            <div>
              <p className={`${typography?.body?.small} ${colors?.text?.secondary}`}>Total Applicants</p>
              <p className={`${typography?.h1} ${colors?.text?.primary}`}>{statsData?.[1]?.value}</p>
            </div>
          </div>
        </UnifiedCard>

        <UnifiedCard variant="default">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <TrendingUp className={`${icons?.lg} text-green-600 dark:text-green-400`} />
            </div>
            <div>
              <p className={`${typography?.body?.small} ${colors?.text?.secondary}`}>Interviews</p>
              <p className={`${typography?.h1} ${colors?.text?.primary}`}>{statsData?.[2]?.value}</p>
            </div>
          </div>
        </UnifiedCard>

        <UnifiedCard variant="default">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <UserCheck className={`${icons?.lg} text-purple-600 dark:text-purple-400`} />
            </div>
            <div>
              <p className={`${typography?.body?.small} ${colors?.text?.secondary}`}>Hired</p>
              <p className={`${typography?.h1} ${colors?.text?.primary}`}>{statsData?.[3]?.value}</p>
            </div>
          </div>
        </UnifiedCard>
      </motion.div>
      {/* Recent Activity Section */}
      <motion.div variants={prefersReducedMotion ? {} : itemVariants}>
        <h2 className={`${typography?.h2} ${colors?.text?.primary} mb-4`}>Recent Activity</h2>
        <UnifiedCard>
          <div className="p-4 md:p-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-2 pb-4 border-b border-border">
                <div className="p-2 bg-green-500/10 rounded-full">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <div className={`${typography?.body?.small} font-medium ${colors?.text?.primary}`}>New application received</div>
                  <div className={`${typography?.caption} ${colors?.text?.secondary}`}>Jane Smith applied for Senior React Developer</div>
                  <div className={`${typography?.caption} ${colors?.text?.secondary}`}>5 minutes ago</div>
                </div>
              </div>
              <div className="flex items-start gap-2 pb-4 border-b border-border">
                <div className="p-2 bg-blue-500/10 rounded-full">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <div className={`${typography?.body?.small} font-medium ${colors?.text?.primary}`}>Interview scheduled</div>
                  <div className={`${typography?.caption} ${colors?.text?.secondary}`}>Technical interview with John Doe tomorrow at 2 PM</div>
                  <div className={`${typography?.caption} ${colors?.text?.secondary}`}>1 hour ago</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="p-2 bg-accent/10 rounded-full">
                  <Briefcase className="w-4 h-4 text-accent" />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <div className={`${typography?.body?.small} font-medium ${colors?.text?.primary}`}>Job posting approved</div>
                  <div className={`${typography?.caption} ${colors?.text?.secondary}`}>React Developer position is now live</div>
                  <div className={`${typography?.caption} ${colors?.text?.secondary}`}>2 hours ago</div>
                </div>
              </div>
            </div>
          </div>
        </UnifiedCard>
      </motion.div>
    </div>
  );
};

export default DashboardOverview;